// src/components/store/sections/product-cards/NeomorphicClayProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, Plus, Check, Heart } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const CLAY_THEMES = [
  {
    cardBg: "bg-[#f0f4f8]",
    wellBg: "bg-[#e2e8f0]",
    accentBg: "bg-[#6366f1]",
    accentText: "text-[#4f46e5]",
    pillBg: "bg-indigo-100 text-indigo-700",
  },
  {
    cardBg: "bg-[#fdf2f8]",
    wellBg: "bg-[#fce7f3]",
    accentBg: "bg-[#ec4899]",
    accentText: "text-[#db2777]",
    pillBg: "bg-pink-100 text-pink-700",
  },
  {
    cardBg: "bg-[#f0fdf4]",
    wellBg: "bg-[#dcfce7]",
    accentBg: "bg-[#10b981]",
    accentText: "text-[#059669]",
    pillBg: "bg-emerald-100 text-emerald-700",
  },
  {
    cardBg: "bg-[#fffbeb]",
    wellBg: "bg-[#fef3c7]",
    accentBg: "bg-[#f59e0b]",
    accentText: "text-[#d97706]",
    pillBg: "bg-amber-100 text-amber-800",
  },
];

export default function NeomorphicClayProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    comparePrice,
    added,
    wishlisted,
    setWishlisted,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = CLAY_THEMES[index % CLAY_THEMES.length];

  return (
    <div
      className={`w-full h-full rounded-[34px] p-4 ${theme.cardBg} border-2 border-white shadow-[8px_8px_20px_rgba(163,177,198,0.35),-8px_-8px_20px_rgba(255,255,255,0.9)] flex flex-col justify-between group transition-all duration-300 hover:-translate-y-1 select-none`}
    >
      <div>
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-3">
          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide shadow-inner ${theme.pillBg}`}>
            {categoryName}
          </span>

          <button
            type="button"
            onClick={() => setWishlisted(!wishlisted)}
            className="w-8 h-8 rounded-2xl bg-white shadow-[4px_4px_10px_rgba(0,0,0,0.06),-4px_-4px_10px_rgba(255,255,255,0.9)] flex items-center justify-center text-slate-400 hover:text-rose-500 cursor-pointer"
          >
            <Heart className={`w-4 h-4 ${wishlisted ? "fill-rose-500 text-rose-500" : ""}`} />
          </button>
        </div>

        {/* Soft Recessed Clay Well for Image */}
        <Link
          href={productUrl}
          className={`relative w-full h-44 rounded-[26px] ${theme.wellBg} shadow-[inset_6px_6px_12px_rgba(0,0,0,0.06),inset_-6px_-6px_12px_rgba(255,255,255,0.8)] flex items-center justify-center p-4 mb-3.5 block`}
        >
          <div className="relative w-36 h-36">
            <Image
              src={productImg}
              alt={productName}
              fill
              unoptimized
              className="object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-xl"
            />
          </div>
        </Link>

        {/* Title & Rating */}
        <div className="px-1 space-y-1">
          <div className="flex items-center gap-1 text-amber-500 text-xs font-black">
            <Star className="w-3.5 h-3.5 fill-amber-400" />
            <span>{rating.toFixed(1)}</span>
            <span className="text-[10px] text-slate-400 font-semibold ml-1">• Soft Clay Edition</span>
          </div>

          <Link href={productUrl} className="block">
            <h3 className="text-sm sm:text-base font-black text-slate-800 truncate hover:opacity-80">
              {productName}
            </h3>
          </Link>
        </div>
      </div>

      {/* Tactile Puffy Bottom Pill Bar */}
      <div className="mt-4 p-2.5 pl-4 rounded-[22px] bg-white shadow-[4px_4px_12px_rgba(0,0,0,0.05)] flex items-center justify-between">
        <div>
          <span className={`text-base sm:text-lg font-black font-mono ${theme.accentText} block leading-none`}>
            ৳{currentPrice.toLocaleString()}
          </span>
          {comparePrice > currentPrice && (
            <span className="text-[10px] text-slate-400 line-through font-mono">
              ৳{comparePrice.toLocaleString()}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className={`px-4 py-2.5 rounded-2xl ${theme.accentBg} text-white font-black text-[11px] uppercase tracking-wider shadow-[0_6px_14px_rgba(0,0,0,0.16)] active:scale-95 transition-all cursor-pointer flex items-center gap-1`}
        >
          {added ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4 stroke-[3]" />}
          <span>{added ? "Added" : "Add"}</span>
        </button>
      </div>
    </div>
  );
}
